import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import { publicAuth } from '@/boot/publicAuth'

// ===============================
// Public API Interfaces
// ===============================

// Base URL configuration
const PUBLIC_API_BASE_URL = '/api/v1/public'

// Create a dedicated axios instance for public API (no auth required)
const publicApi = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add Firebase auth token if user is authenticated
publicApi.interceptors.request.use(
  async (config) => {
    console.log('Public API request:', config.url)
    
    // Check for token in localStorage first
    const token = localStorage.getItem('publicAuthToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Add domain if available (for multi-tenant support)
    const domain = localStorage.getItem('domain')
    if (domain) {
      config.baseURL = domain
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling with retry logic
publicApi.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config
    
    // Handle 401 Unauthorized errors with retry logic
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1
      
      // Max 3 retry attempts
      if (originalRequest._retryCount <= 3) {
        try {
          // Try to refresh the Firebase token
          const newToken = await publicAuth.refreshToken()
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            console.log(`Retrying request with refreshed token (attempt ${originalRequest._retryCount}/3)`)
            return publicApi(originalRequest)
          }
        } catch (refreshError) {
          console.warn(`Failed to refresh Firebase token (attempt ${originalRequest._retryCount}/3):`, refreshError)
          
          // If we haven't reached max retries, try again
          if (originalRequest._retryCount < 3) {
            // Wait a bit before retrying
            await new Promise(resolve => setTimeout(resolve, 1000 * originalRequest._retryCount))
            return publicApi(originalRequest)
          }
        }
      }
      
      // If all retries failed, clear the invalid token
      console.error('All token refresh attempts failed, clearing stored token')
      localStorage.removeItem('publicAuthToken')
    }
    
    return Promise.reject(error)
  }
)

// ===============================
// Service Interfaces
// ===============================
export interface PublicService {
  id: number
  title: string
  description: string | null
  duration: number
  cost: number
  color: string
  active: boolean
  hidden: boolean
  image: string | null
  location: 'In Person' | 'Online' | null
  order: number
  bffer: number
  category: number[]
  team_members: number[]
  created: string
  updated: string
}

// ===============================
// Team Member Interfaces
// ===============================
export interface WorkingHours {
  day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'
  start_time: string
  end_time: string
  is_working_day: boolean
}

export interface PublicTeamMember {
  id: number
  first_name: string
  last_name: string
  email: string
  phone: string | null
  country_code: string
  image: string | null
  permission: 'NO_ACCESS' | 'STANDARD' | 'ENHANCED' | 'ADMIN'
  order: number
  is_active: boolean
  services: number[]
  working_hours: WorkingHours[]
  available_timeslots_by_day: Record<string, string[]>
  default_calendar: number
}

// ===============================
// Booking Preferences Interfaces
// ===============================
export interface BookingPreferences {
  id: number
  back_image: string | null
  appointment_lead_time: number
  appointment_lead_time_unit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years'
  booking_slot_size: number
  booking_slot_size_unit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years'
  scheduling_window: number
  scheduling_window_unit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years'
  cancellation_policy: string
  first_available: boolean
  skip_team_member: boolean
  multiple_services: boolean
  any_team_member: boolean
  customer_login: boolean
  customer_login_required: boolean
  allow_reschedule: boolean
  allow_cancel: boolean
  allow_booking: boolean
  default_language: string
  time_format: '12' | '24'
  week_start: 'monday' | 'sunday'
}

// ===============================
// Business Info Interfaces
// ===============================
export interface BusinessLocation {
  address: string
  city: string
  country: string
  postal_code: string
  latitude: number
  longitude: number
  name: string
  url: string
}

export interface BusinessInfo {
  id: number
  business_name: string
  phone: string | null
  phone2: string | null
  email: string | null
  email2: string | null
  timezone: string
  currency: string | null
  industry: string | null
  about: string | null
  website: string | null
  location: BusinessLocation | null
  image: string | null
  facebook: string | null
  instagram: string | null
  twitter: string | null
  linkedin: string | null
  youtube: string | null
  working_hours: WorkingHours[]
}

// ===============================
// Public Data Response Interface
// ===============================
export interface PublicDataResponse {
  services: PublicService[]
  team: PublicTeamMember[]
  preferences: BookingPreferences
  info: BusinessInfo
  interval: number
  timezone: number
  location: BusinessLocation | null
  /**
   * Authoritative tenant schema_name resolved by the backend from the request
   * host. Use this instead of parsing the subdomain — it is correct even when
   * the tenant is reached via a non-primary alias domain (e.g. a vanity
   * subdomain like `move-pilates.manito-apt.cc` that points at schema
   * `askenderfitnessworld`).
   */
  schema_name: string | null
  /** Tenant primary key. May be null if the request did not resolve to a tenant. */
  tenant_id: number | null
}

// ===============================
// Appointment Interfaces
// ===============================
export interface CustomerData {
  id: number
  full_name: string
  phone: string | null
  email: string | null
  image: string | null
  address: string | null
  city: string | null
}

export interface AppointmentService {
  id: number
  title: string
  duration: number
  cost: number
  image: string | null
}

export interface AppointmentTeamMember {
  id: number
  user: number
  image: string | null
  first_name: string
  last_name: string
}

export interface UserAppointment {
  id: number
  start: string
  end: string
  title: string
  team_member: AppointmentTeamMember
  service: AppointmentService
  canceled: boolean
  notes?: string | null
  online_meta?: {
    token: string
    expiration: string
  }
}

export interface ServiceData {
  id: number
  title: string
  color: string
  duration: number
  bffer: number
  cost: number
}

export interface TeamMemberData {
  id: number
  first_name: string
  last_name: string
  email: string
  phone: string | null
  image: string | null
}

export interface OnlineMeta {
  token: string
  expiration: string
}

export interface AppointmentDetails {
  id: number
  title: string
  start: string
  end: string
  customerEnd: string
  duration: {
    minutes: number
  }
  notes: string | null
  canceled: boolean
  start_date: string
  customer: number
  service: number
  calendar: number
  calendar_id: number
  team_member_id: number
  customer_data: CustomerData
  service_data: ServiceData
  teamMember: TeamMemberData
  online_meta: OnlineMeta
}

// ===============================
// Appointment Creation/Update Interfaces
// ===============================
export interface AppointmentCustomer {
  name: string
  phone?: string | null
  countryCode?: string | null
  email?: string | null
  address?: string | null
  city?: string | null
}

export interface AppointmentRequest {
  id?: number | null // For updates/cancellations
  teamMember: {
    id: number
  }
  service: {
    id: number
  }
  customer: AppointmentCustomer
  start: string // ISO 8601 format
  canceled?: boolean
  notes?: string | null
}

export interface AppointmentResponse {
  id: number
  teamMember: {
    id: number
  }
  service: {
    id: number
  }
  customer: {
    name: string
    phone: string | null
    email: string | null
    address: string | null
    city: string | null
  }
  start: string
  end: string
  customerEnd: string
  notes: string | null
  online_meta: {
    token: string
    expiration: string
  }
}

// ===============================
// Availability Interfaces
// ===============================
export interface AvailabilityRequest {
  team_member: number
  service: number
  date: string // YYYY-MM-DD or DD/MM/YYYY
}

export interface AvailabilityResponse {
  [date: string]: string[] // Date as YYYY-MM-DD, values as time slots in HH:MM
}

// ===============================
// Error Response Interface
// ===============================
export interface ApiErrorResponse {
  error: string
  details?: Record<string, any>
}

// ===============================
// User Profile Interface
// ===============================
export interface PublicUserProfile {
  id: number
  email: string
  first_name?: string
  last_name?: string
  full_name?: string
  phone?: string | null
  country_code?: string | null
  image?: string | null
  domain?: string
  preferences?: Record<string, any>
}

// ===============================
// Public Store
// ===============================
export const usePublicStore = defineStore('public', () => {
  // State
  const publicData = ref<PublicDataResponse | null>(null)
  const userProfile = ref<PublicUserProfile | null>(null)
  const isLoading = ref(false)

  // Computed getters
  const allowBooking = computed(() => publicData.value?.preferences?.allow_booking ?? true)
  
  // Track pending requests to prevent race conditions
  const pendingPublicDataRequest = ref<Promise<PublicDataResponse> | null>(null)
  const pendingUserProfileRequest = ref<Promise<PublicUserProfile | null> | null>(null)

  // ===============================
  // Actions
  // ===============================

  /**
   * Get comprehensive public business data with intelligent caching
   * @param force - Force refresh even if data exists
   * @returns Promise<PublicDataResponse>
   */
  const getPublicData = async (force = false): Promise<PublicDataResponse> => {
    // Return cached data if available and not forcing refresh
    if (!force && publicData.value) {
      return publicData.value
    }

    // Return existing pending request if one is already in progress
    if (pendingPublicDataRequest.value) {
      return pendingPublicDataRequest.value
    }

    // Create new request
    isLoading.value = true
    pendingPublicDataRequest.value = (async () => {
      try {
        const response = await publicApi.get<PublicDataResponse>(`${PUBLIC_API_BASE_URL}/`)
        publicData.value = response.data
        return response.data
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || 'Failed to load business data'
        throw new Error(errorMessage)
      } finally {
        isLoading.value = false
        pendingPublicDataRequest.value = null
      }
    })()

    return pendingPublicDataRequest.value
  }

  /**
   * Create a new appointment
   * @param appointmentData - Appointment creation data
   * @returns Promise<AppointmentResponse>
   */
  const createAppointment = async (appointmentData: AppointmentRequest): Promise<AppointmentResponse> => {
    isLoading.value = true

    try {
      const response = await publicApi.post<AppointmentResponse>(
        `${PUBLIC_API_BASE_URL}/appointment/`,
        appointmentData
      )
      return response.data
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to create appointment'
      // Throw the original error to preserve the response structure
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Update an existing appointment
   * @param appointmentData - Appointment update data (must include id)
   * @returns Promise<AppointmentResponse>
   */
  const updateAppointment = async (appointmentData: AppointmentRequest): Promise<AppointmentResponse> => {
    if (!appointmentData.id) {
      throw new Error('Appointment ID is required for updates')
    }

    isLoading.value = true

    try {
      const response = await publicApi.post<AppointmentResponse>(
        `${PUBLIC_API_BASE_URL}/appointment/`,
        appointmentData
      )
      return response.data
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to update appointment'
      // Throw the original error to preserve the response structure
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Cancel an existing appointment
   * @param appointmentId - Appointment ID to cancel
   * @returns Promise<AppointmentResponse>
   */
  const cancelAppointment = async (appointmentId: number): Promise<AppointmentResponse> => {
    isLoading.value = true

    try {
      const response = await publicApi.post<AppointmentResponse>(
        `${PUBLIC_API_BASE_URL}/appointment/`,
        {
          id: appointmentId,
          canceled: true
        }
      )
      return response.data
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to cancel appointment'
      // Throw the original error to preserve the response structure
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get appointment details by ID and token
   * @param appointmentId - Appointment ID
   * @param token - Authentication token
   * @returns Promise<AppointmentDetails>
   */
  const getAppointmentDetails = async (appointmentId: string, token: string): Promise<AppointmentDetails> => {
    isLoading.value = true

    try {
      const response = await publicApi.get<AppointmentDetails>(
        `${PUBLIC_API_BASE_URL}/appointment/${appointmentId}/`,
        {
          params: { token }
        }
      )
      return response.data
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to get appointment details'
      throw new Error(errorMessage)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Alternative method to get appointment details using different endpoint
   * @param appointmentId - Appointment ID  
   * @param token - Authentication token
   * @returns Promise<AppointmentDetails>
   */
  const getAppointmentDetailsAlt = async (appointmentId: string, token: string): Promise<AppointmentDetails> => {
    isLoading.value = true

    try {
      const response = await publicApi.get<AppointmentDetails>(
        `${PUBLIC_API_BASE_URL}/appointment/`,
        {
          params: { 
            id: appointmentId,
            token 
          }
        }
      )
      return response.data
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to get appointment details'
      throw new Error(errorMessage)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get team member availability for a specific service and month
   * @param availabilityData - Availability request data
   * @returns Promise<AvailabilityResponse>
   */
  const getAvailability = async (availabilityData: AvailabilityRequest): Promise<AvailabilityResponse> => {
    // Don't use global isLoading for availability requests - 
    // this prevents the main loading screen from showing during calendar navigation
    // and avoids VDatePicker re-renders that reset the calendar

    try {
      const response = await publicApi.post<AvailabilityResponse>(
        `${PUBLIC_API_BASE_URL}/availability/`,
        availabilityData
      )
      return response.data
    } catch (err: any) {
      // Preserve the original error with status code so callers can distinguish
      // between network errors and booking-disabled (403) responses
      throw err
    }
  }

  /**
   * Get authenticated user profile with intelligent caching
   * Only calls API if there's a token in localStorage and no cached data exists
   * @param force - Force refresh even if data exists
   * @returns Promise<PublicUserProfile | null>
   */
  const getUserProfile = async (force = false): Promise<PublicUserProfile | null> => {
    // Check if user is authenticated
    const token = localStorage.getItem('publicAuthToken')
    if (!token) {
      userProfile.value = null
      return null
    }

    // Return cached data if available and not forcing refresh
    if (!force && userProfile.value) {
      return userProfile.value
    }

    // Return existing pending request if one is already in progress
    if (pendingUserProfileRequest.value) {
      return pendingUserProfileRequest.value
    }

    // Create new request
    isLoading.value = true
    pendingUserProfileRequest.value = (async () => {
      try {
        const response = await publicApi.get<PublicUserProfile>(`${PUBLIC_API_BASE_URL}/me/`)
        userProfile.value = response.data
        
        // Update domain in localStorage if provided
        if (response.data.domain) {
          localStorage.setItem('domain', response.data.domain)
        }
        
        return response.data
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || 'Failed to get user profile'
        userProfile.value = null
        
        // If 401, the token is invalid, so clear it
        if (err.response?.status === 401) {
          localStorage.removeItem('publicAuthToken')
        }
        
        // If 404, user profile doesn't exist - handle differently based on current route
        if (err.response?.status === 404) {
          // Dynamic import to check current route without circular dependency
          return handleUserProfileNotFound()
        }
        
        console.warn('Failed to fetch user profile:', errorMessage)
        return null
      } finally {
        isLoading.value = false
        pendingUserProfileRequest.value = null
      }
    })()

    return pendingUserProfileRequest.value
  }

  /**
   * Handle 404 user profile not found response
   * @returns Promise<PublicUserProfile | null>
   */
  const handleUserProfileNotFound = async (): Promise<PublicUserProfile | null> => {
    try {
      // Always return null and let the components handle profile creation
      // DO NOT sign out users when profile is not found
      console.log('User profile not found (404), user may need to create one during booking')
      return null
    } catch (error) {
      console.error('Error handling user profile not found:', error)
      return null
    }
  }

  /**
   * Create a new user profile
   * @param profileData - User profile data
   * @returns Promise<PublicUserProfile>
   */
  const createUserProfile = async (profileData: Partial<PublicUserProfile>): Promise<PublicUserProfile> => {
    // Check if user is authenticated
    const token = localStorage.getItem('publicAuthToken')
    if (!token) {
      throw new Error('Authentication required to create user profile')
    }

    isLoading.value = true

    try {
      const response = await publicApi.post<PublicUserProfile>(`${PUBLIC_API_BASE_URL}/me/`, profileData)
      userProfile.value = response.data
      
      // Update domain in localStorage if provided
      if (response.data.domain) {
        localStorage.setItem('domain', response.data.domain)
      }
      
      console.log('User profile created successfully:', response.data)
      return response.data
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to create user profile'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get authenticated user's appointments with pagination
   * @param params - Optional query parameters for filtering and pagination
   * @returns Promise<{count: number, results: UserAppointment[]}>
   */
  const getUserAppointments = async (params: {
    limit?: number
    offset?: number
    status?: string
    append?: boolean
  } = {}): Promise<{count: number, results: UserAppointment[]}> => {
    // Check if user is authenticated
    const token = localStorage.getItem('publicAuthToken')
    if (!token) {
      throw new Error('Authentication required to view appointments')
    }

    isLoading.value = true

    try {
      const response = await publicApi.get(`${PUBLIC_API_BASE_URL}/appointments/`, {
        params: {
          limit: params.limit || 25,
          offset: params.offset || 0,
          status: params.status
        }
      })
      return response.data
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to get user appointments'
      throw new Error(errorMessage)
    } finally {
      isLoading.value = false
    }
  }

  // ===============================
  // Initialization & Cache Management
  // ===============================

  /**
   * Initialize public data - should be called once by the layout
   * This method ensures both public data and user profile are loaded if needed
   */
  const initializePublicData = async (): Promise<void> => {
    try {
      // Load public data if not already cached
      if (!publicData.value) {
        await getPublicData()
      }

      // Load user profile if user is authenticated and not already cached
      const token = localStorage.getItem('publicAuthToken')
      if (token && !userProfile.value) {
        await getUserProfile()
      }
    } catch (error) {
      console.error('Failed to initialize public data:', error)
      // Don't throw - let individual components handle their own error states
    }
  }

  /**
   * Check if public data is available (cached)
   */
  const hasPublicData = (): boolean => {
    return publicData.value !== null
  }

  /**
   * Check if user profile is available (cached)
   */
  const hasUserProfile = (): boolean => {
    return userProfile.value !== null
  }

  /**
   * Get cached public data without making API call
   */
  const getCachedPublicData = (): PublicDataResponse | null => {
    return publicData.value
  }

  /**
   * Get cached user profile without making API call
   */
  const getCachedUserProfile = (): PublicUserProfile | null => {
    return userProfile.value
  }

  // ===============================
  // Utility Methods
  // ===============================

  /**
   * Clear any cached public data and force refresh on next request
   */
  const clearPublicData = () => {
    publicData.value = null
    pendingPublicDataRequest.value = null
  }

  /**
   * Clear user profile data and force refresh on next request
   */
  const clearUserProfile = () => {
    userProfile.value = null
    pendingUserProfileRequest.value = null
  }

  /**
   * Clear all cached data
   */
  const clearAllCache = () => {
    clearPublicData()
    clearUserProfile()
  }

  /**
   * Clear any error state
   */
  const clearError = () => {
    // No-op since we removed error state
  }

  /**
   * Check if a team member can perform a specific service
   * @param teamMemberId - Team member ID
   * @param serviceId - Service ID
   * @returns boolean
   */
  const canTeamMemberPerformService = (teamMemberId: number, serviceId: number): boolean => {
    if (!publicData.value) return false
    
    const teamMember = publicData.value.team.find(tm => tm.id === teamMemberId)
    return teamMember?.services.includes(serviceId) || false
  }

  /**
   * Get all team members qualified for a specific service
   * @param serviceId - Service ID
   * @returns PublicTeamMember[]
   */
  const getTeamMembersForService = (serviceId: number): PublicTeamMember[] => {
    if (!publicData.value) return []
    
    return publicData.value.team.filter(tm => 
      tm.is_active && tm.services.includes(serviceId)
    )
  }

  /**
   * Get all services available for a specific team member
   * @param teamMemberId - Team member ID
   * @returns PublicService[]
   */
  const getServicesForTeamMember = (teamMemberId: number): PublicService[] => {
    if (!publicData.value) return []
    
    const teamMember = publicData.value.team.find(tm => tm.id === teamMemberId)
    if (!teamMember) return []
    
    return publicData.value.services.filter(service => 
      service.active && !service.hidden && teamMember.services.includes(service.id)
    )
  }

  /**
   * Format time from 24-hour to 12-hour format if preferences require it
   * @param time - Time in HH:MM format
   * @returns Formatted time string
   */
  const formatTime = (time: string): string => {
    if (!publicData.value?.preferences.time_format || publicData.value.preferences.time_format === '24') {
      return time
    }
    
    const [hours, minutes] = time.split(':').map(Number)
    const period = hours >= 12 ? 'PM' : 'AM'
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
    
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`
  }

  return {
    // State
    publicData,
    userProfile,
    isLoading,
    allowBooking,
    
    // Actions
    getPublicData,
    createAppointment,
    updateAppointment,
    cancelAppointment,
    getAppointmentDetails,
    getAppointmentDetailsAlt,
    getAvailability,
    getUserProfile,
    createUserProfile,
    getUserAppointments,
    
    // Initialization & Cache Management
    initializePublicData,
    hasPublicData,
    hasUserProfile,
    getCachedPublicData,
    getCachedUserProfile,
    clearPublicData,
    clearUserProfile,
    clearAllCache,
    
    // Utility methods
    clearError,
    canTeamMemberPerformService,
    getTeamMembersForService,
    getServicesForTeamMember,
    formatTime
  }
})
